import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { DashboardLayout } from '../components/layout/DashboardSidebar';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';

const TestZonePage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // Read initial type from query string (e.g. /test-zone?type=Practice)
  const initialType = searchParams.get('type') || 'Mock';

  const [courses, setCourses]           = useState([]);
  const [selectedCourse, setSelectedCourse] = useState('');
  const [testType, setTestType]         = useState(initialType);
  const [selectedLang, setSelectedLang] = useState('English');
  const [topics, setTopics]             = useState([]);
  const [selectedTopic, setSelectedTopic] = useState(null);
  const [quizzes, setQuizzes]           = useState([]);
  const [loading, setLoading]           = useState(false);

  // Load subscribed courses only
  useEffect(() => {
    api.get('/users/subscriptions')
      .then(res => {
        const subs = res.data.data || [];
        const map  = new Map();
        subs.forEach(s => {
          if (!map.has(s.CourseId)) map.set(s.CourseId, { CourseId: s.CourseId, CourseName: s.CourseName || s.PackTitle || 'Course' });
        });
        const list = Array.from(map.values());
        setCourses(list);

        // Restore from session or default
        const saved = sessionStorage.getItem('activeCourse');
        if (saved && list.some(c => String(c.CourseId) === saved)) {
          setSelectedCourse(saved);
        } else if (list.length > 0) {
          setSelectedCourse(String(list[0].CourseId));
        }
      })
      .catch(console.error);
  }, []);

  // Load topics when course or test type changes
  useEffect(() => {
    if (!selectedCourse) return;
    setQuizzes([]);
    setSelectedTopic(null);
    api.get(`/test-zone/topics?courseId=${selectedCourse}&type=${testType}`)
      .then(res => { if (res.data.success) setTopics(res.data.data); })
      .catch(console.error);
  }, [selectedCourse, testType]);

  const fetchQuizzes = async (topicId, lang) => {
    setLoading(true);
    try {
      const res = await api.get(`/test-zone/quizzes?courseId=${selectedCourse}&topicId=${topicId}&type=${testType}&lang=${lang}`);
      if (res.data.success) setQuizzes(res.data.data);
    } catch (e) { console.error(e); }
    setLoading(false);
  };

  const handleTopicClick = (topicId) => {
    setSelectedTopic(topicId);
    fetchQuizzes(topicId, selectedLang);
  };

  const handleLangChange = (e) => {
    const lang = e.target.value;
    setSelectedLang(lang);
    if (selectedTopic) fetchQuizzes(selectedTopic, lang);
  };

  const handleCourseChange = (e) => {
    const v = e.target.value;
    setSelectedCourse(v);
    sessionStorage.setItem('activeCourse', v);
  };

  return (
    <DashboardLayout title="Test Zone">
      <div className="p-6 md:p-8 max-w-7xl mx-auto">
        <h1 className="text-3xl font-bold mb-8 flex items-center gap-3">
          <span className="text-4xl">📝</span> Test Zone
        </h1>

        {/* Filters */}
        <div className="bg-white/10 backdrop-blur-md rounded-2xl p-6 border border-white/10 mb-8 flex flex-wrap gap-6 items-end">
          <div className="flex-1 min-w-[200px]">
            <label className="block text-text-muted text-sm mb-2">Select Course</label>
            <select
              className="w-full bg-[#2d1b4e] border border-white/20 rounded-lg p-3 text-white focus:border-accent-gold outline-none cursor-pointer"
              value={selectedCourse}
              onChange={handleCourseChange}
            >
              <option value="">Select Course</option>
              {courses.map(c => <option key={c.CourseId} value={c.CourseId}>{c.CourseName}</option>)}
            </select>
          </div>

          <div className="w-[150px]">
            <label className="block text-text-muted text-sm mb-2">Language</label>
            <select
              className="w-full bg-[#2d1b4e] border border-white/20 rounded-lg p-3 text-white focus:border-accent-gold outline-none cursor-pointer"
              value={selectedLang}
              onChange={handleLangChange}
            >
              <option value="English">English</option>
              <option value="Hindi">Hindi</option>
              <option value="Assamese">Assamese</option>
            </select>
          </div>

          <div className="flex-2">
            <label className="block text-text-muted text-sm mb-2">Test Type</label>
            <div className="flex bg-[#2d1b4e] rounded-lg p-1">
              {['Practice', 'Mock', 'Free'].map(type => (
                <button
                  key={type}
                  onClick={() => setTestType(type)}
                  className={`px-6 py-2 rounded-md text-sm font-medium transition-all ${
                    testType === type
                      ? 'bg-accent-gold text-black shadow-lg'
                      : 'text-text-muted hover:text-white'
                  }`}
                >
                  {type} Tests
                </button>
              ))}
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
          {/* Topics */}
          <div className="lg:col-span-1 space-y-3">
            <h3 className="font-bold text-xl mb-4 text-accent-gold">Topics ({topics.length})</h3>
            <div className="max-h-[600px] overflow-y-auto pr-2 space-y-2">
              {topics.length > 0 ? topics.map(topic => (
                <button
                  key={topic.TopicId}
                  onClick={() => handleTopicClick(topic.TopicId)}
                  className={`w-full text-left p-4 rounded-xl border transition-all flex items-center justify-between ${
                    selectedTopic === topic.TopicId
                      ? 'bg-gradient-to-r from-purple-600 to-blue-600 border-accent-gold/50 shadow-lg'
                      : 'bg-white/5 border-white/5 hover:bg-white/10'
                  }`}
                >
                  <span className="font-medium">{topic.TopicName}</span>
                  {selectedTopic === topic.TopicId && <span>👉</span>}
                </button>
              )) : (
                <div className="text-gray-400 text-sm p-4 border border-dashed border-white/10 rounded-xl">
                  {selectedCourse ? 'No topics found for this course and test type.' : 'Please select a course.'}
                </div>
              )}
            </div>
          </div>

          {/* Quizzes */}
          <div className="lg:col-span-3">
            {loading ? (
              <div className="text-center py-20 text-text-muted animate-pulse">Loading Tests...</div>
            ) : quizzes.length === 0 ? (
              <div className="text-center py-20 text-text-muted bg-white/5 rounded-2xl border border-white/5 border-dashed">
                {selectedTopic
                  ? `No ${selectedLang} tests available for this topic.`
                  : 'Select a topic from the list to view tests.'}
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {quizzes.map(quiz => (
                  <div key={quiz.QuizId}
                    className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-6 hover:border-accent-gold/30 transition-all group relative overflow-hidden">
                    <div className="absolute top-0 right-0 p-3 opacity-10 group-hover:opacity-20 transition-opacity">
                      <span className="text-6xl">📝</span>
                    </div>
                    <div className="flex justify-between items-start mb-4">
                      <span className="bg-blue-500/20 text-blue-300 text-xs px-2 py-1 rounded border border-blue-500/20">
                        {quiz.QuizCode}
                      </span>
                      {quiz.status === 'Completed' && (
                        <span className="bg-green-500/20 text-green-300 text-xs px-2 py-1 rounded border border-green-500/20">
                          Score: {quiz.lastScore}/{quiz.TotalMarks}
                        </span>
                      )}
                    </div>
                    <h3 className="text-xl font-bold mb-2 line-clamp-2">{quiz.Title}</h3>
                    <div className="grid grid-cols-2 gap-y-2 text-sm text-text-muted mb-6">
                      <div>⏱️ {quiz.MaxTime} Mins</div>
                      <div>❓ {quiz.MaxNOQ} Questions</div>
                      <div>🎯 {quiz.TotalMarks} Marks</div>
                      <div>👥 {quiz.Attendees} Students</div>
                    </div>
                    <button
                      onClick={() => navigate(`/${quiz.testEnginePage}/${quiz.QuizId}`)}
                      className="w-full btn-primary py-3 rounded-xl font-bold shadow-lg shadow-purple-900/20"
                    >
                      {quiz.status === 'Completed' ? 'Retake Test' : 'Start Now'}
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default TestZonePage;
